import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { addRosterPatient } from "@/lib/noteClaim";
import { useClient } from "@/lib/clientContext";
import { IconLens } from "@/components/icons/revqIcons";
import { uploadDocument, getJob, getAnalysis, fileToBase64, genderCode, MAX_FILE_BYTES, ACCEPTED } from "@/lib/luminaDocs";

// Add patient as its own tab page (not a lightbox). §4 exact values, no shared-modal inheritance.
const toISO = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return s;
};
// ISO (YYYY-MM-DD) → MM/DD/YYYY for the editable text field; anything else passes through.
const toDisplayDob = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1]}`;
  return s;
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const ACCEPT_ATTR = ACCEPTED.map((m) => "." + m.split("/")[1]).join(",");

// Keys Lens may emit on an ID / insurance card, mapped to our form fields.
const EXTRACT_KEYS = {
  first: ["first_name", "patient_first_name", "given_name"],
  last: ["last_name", "patient_last_name", "family_name", "surname"],
  full: ["patient_name", "patient_full_name", "full_name", "name", "member_name", "subscriber_name"],
  dob: ["date_of_birth", "dob", "birth_date", "patient_dob"],
  gender: ["sex", "gender", "patient_sex"],
  phone: ["phone", "phone_number", "patient_phone", "home_phone", "contact_number", "mobile"],
  payer: ["payer_name", "payer", "insurance", "insurer", "insurance_company", "plan_name", "carrier", "carrier_name"],
  member: ["member_id", "member_number", "subscriber_id", "policy_number", "id_number", "insurance_id"],
};
function flattenLeaves(obj, out = {}) {
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || v === "") continue;
    if (Array.isArray(v)) { if (out[k] == null && v.length) out[k] = v[0]; }
    else if (typeof v === "object") flattenLeaves(v, out);
    else if (out[k] == null) out[k] = v;
  }
  return out;
}
function pickExtraction(extraction) {
  const leaves = {};
  for (const [k, v] of Object.entries(flattenLeaves(extraction || {}))) leaves[k.toLowerCase()] = v;
  const get = (keys) => { for (const k of keys) { const v = leaves[k]; if (v != null && String(v).trim()) return String(v).trim(); } return ""; };
  let first = get(EXTRACT_KEYS.first), last = get(EXTRACT_KEYS.last);
  if (!first && !last) { const full = get(EXTRACT_KEYS.full); if (full) { const parts = full.split(/\s+/); first = parts[0] || ""; last = parts.slice(1).join(" ") || ""; } }
  return {
    first_name: first, last_name: last,
    dob: toDisplayDob(get(EXTRACT_KEYS.dob)),
    gender: genderCode(get(EXTRACT_KEYS.gender)),
    phone: get(EXTRACT_KEYS.phone),
    payer_name: get(EXTRACT_KEYS.payer),
    member_id: get(EXTRACT_KEYS.member),
  };
}

const CSS = `
.apf{padding:24px 28px 80px;display:flex;flex-direction:column;gap:16px;max-width:860px;}
.apf-crumb{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:#6C6C69;background:none;border:0;cursor:pointer;align-self:flex-start;transition:color .12s;}
.apf-crumb:hover{color:#141412;}
.apf-head{display:flex;flex-direction:column;gap:6px;}
.apf-head h1{font-size:28px;font-weight:700;letter-spacing:-0.01em;color:#141412;margin:0;line-height:1.15;}
.apf-lede{font-size:13.5px;color:#6C6C69;}
.apf-lens{background:#fff;border:1.5px dashed #E9E9EC;border-radius:14px;padding:16px 18px;display:flex;flex-wrap:wrap;align-items:center;gap:14px;transition:border-color .12s,background-color .12s;}
.apf-lens.drag{border-color:#9B9B97;background:#FBFBFD;}
.apf-lens-ic{flex:none;color:#141412;display:flex;}
.apf-lens-mid{flex:1;min-width:200px;display:flex;flex-direction:column;gap:2px;}
.apf-lens-title{font-size:13px;font-weight:700;color:#141412;}
.apf-lens-sub{font-size:12px;color:#6E6E6B;}
.apf-lens-right{display:flex;flex-direction:column;align-items:flex-start;gap:4px;}
.apf-choose{height:34px;padding:0 13px;border-radius:10px;border:1px solid #E9E9EC;background:#fff;font-size:13px;font-weight:600;color:#141412;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:background-color .12s;}
.apf-choose:hover{background:#F1F1F4;}
.apf-choose:focus-within{outline:2px solid #5856D6;outline-offset:2px;}
.apf-choose[aria-disabled="true"]{opacity:.5;cursor:default;}
.apf-hiddenfile{position:absolute;width:1px;height:1px;opacity:0;overflow:hidden;}
.apf-ordrop{font-size:12px;color:#6E6E6B;}
.apf-chips{flex-basis:100%;display:flex;flex-wrap:wrap;gap:6px;padding-left:40px;}
.apf-chip{height:24px;padding:0 9px;border-radius:5px;background:#F1F1F4;color:#141412;font-size:12px;display:inline-flex;align-items:center;gap:5px;max-width:260px;}
.apf-chip-nm{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.apf-card{background:#fff;border:1px solid #E9E9EC;border-radius:14px;padding:18px;display:flex;flex-direction:column;gap:18px;}
.apf-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px 16px;}
.apf-field{display:flex;flex-direction:column;gap:6px;}
.apf-field label{font-size:12px;font-weight:600;color:#6C6C69;}
.apf-input{height:34px;padding:0 10px;border-radius:8px;border:1px solid #E9E9EC;background:#fff;font-size:13px;color:#141412;outline:none;width:100%;transition:border-color .12s,box-shadow .12s;}
.apf-input:focus{border-color:#5856D6;box-shadow:0 0 0 3px rgba(88,86,214,.15);}
.apf-foot{display:flex;align-items:center;gap:10px;}
.apf-primary{height:34px;padding:0 14px;border-radius:10px;background:#141412;color:#fff;font-size:13px;font-weight:600;border:0;cursor:pointer;transition:background-color .12s;}
.apf-primary:hover{background:#2A2A28;}
.apf-primary:disabled{opacity:.4;cursor:default;}
.apf-secondary{height:34px;padding:0 13px;border-radius:10px;border:1px solid #E9E9EC;background:#fff;font-size:13px;font-weight:600;color:#141412;cursor:pointer;transition:background-color .12s;}
.apf-secondary:hover{background:#F1F1F4;}
.apf-dups{border:1px solid #F0D8A8;border-radius:11px;padding:12px;background:#fff;}
`;

export default function AddPatientPage({ prefill, focus, originLabel, onBack, onClose, onCreated }) {
  const { selectedClientId } = useClient();
  const [f, setF] = useState({
    first_name: prefill?.first || "", last_name: prefill?.last || "",
    dob: "", gender: "", phone: "", payer_name: "", member_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [dups, setDups] = useState(null);
  const [scanFiles, setScanFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [dragOn, setDragOn] = useState(false);
  const firstRef = useRef(null);
  const dobRef = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // Prefill can change when the tab is reused with a new typed term.
  useEffect(() => { setF((s) => ({ ...s, first_name: prefill?.first || s.first_name, last_name: prefill?.last || s.last_name })); }, [prefill]);
  useEffect(() => { const t = setTimeout(() => { (focus === "dob" ? dobRef : firstRef).current?.focus(); }, 60); return () => clearTimeout(t); }, [focus]);

  // §4c — send chosen/dropped files to Lens; extracted values fill the editable fields. No auto-submit.
  const scan = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    if (!selectedClientId) { toast.error("Select a client first"); return; }
    const ok = arr.filter((file) => file.size <= MAX_FILE_BYTES);
    if (ok.length < arr.length) toast.error("Some files are larger than 10 MB and were skipped");
    if (!ok.length) return;
    setScanFiles(ok.map((file) => file.name));
    setScanning(true);
    let filled = false;
    try {
      for (const file of ok) {
        const base64_data = await fileToBase64(file);
        const up = await uploadDocument({ client_id: selectedClientId, filename: file.name, content_type: file.type || "application/pdf", base64_data });
        let analysisId = null;
        for (let i = 0; i < 45; i++) {
          await sleep(2000);
          const j = await getJob(up.job_id);
          if (j.status === "completed") { analysisId = j.analysis_id; break; }
          if (j.status === "failed") break;
        }
        if (!analysisId) continue;
        const row = await getAnalysis(analysisId);
        const vals = pickExtraction(row.extraction);
        const nonEmpty = Object.fromEntries(Object.entries(vals).filter(([, v]) => v));
        if (Object.keys(nonEmpty).length) { filled = true; setF((s) => ({ ...s, ...nonEmpty })); }
      }
      toast[filled ? "success" : "message"](filled ? "Lens filled the form from your card" : "Lens couldn't read patient details — type them in");
    } catch { toast.error("Couldn't read the card"); }
    finally { setScanning(false); }
  };

  const submit = async (force) => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.dob.trim()) return;
    setBusy(true); setDups(null);
    const body = { first_name: f.first_name.trim(), last_name: f.last_name.trim(), dob: toISO(f.dob) };
    if (f.gender) body.gender = f.gender;
    if (f.phone.trim()) body.phone = f.phone.trim();
    if (f.payer_name.trim()) body.payer_name = f.payer_name.trim();
    if (f.member_id.trim()) body.member_id = f.member_id.trim();
    if (force) body.force = true;
    try {
      const res = await addRosterPatient(selectedClientId, body);
      const name = res?.patient?.full_name || `${f.first_name.trim()} ${f.last_name.trim()}`;
      const chartUuid = res?.patient?.chart_uuid || res?.patient?.chart_id || res?.chart_uuid || res?.chart_id || null;
      onCreated?.(name, chartUuid);
    } catch (e) {
      if (e?.response?.status === 409) setDups(e.response.data?.detail?.duplicates || []);
    } finally { setBusy(false); }
  };

  const Field = ({ k, label, type, placeholder, inputRef, children }) => (
    <div className="apf-field">
      <label htmlFor={`apf-${k}`}>{label}</label>
      {children || <input id={`apf-${k}`} ref={inputRef} type={type || "text"} value={f[k]} onChange={set(k)} placeholder={placeholder} className="apf-input" autoComplete="off" data-testid={`add-patient-${k}`} />}
    </div>
  );

  return (
    <div className="apf" data-testid="add-patient-page">
      <style>{CSS}</style>
      <button className="apf-crumb" onClick={onBack} data-testid="add-patient-breadcrumb"><ChevronLeft size={15} strokeWidth={1.75} /> {originLabel || "Back"}</button>

      <div className="apf-head">
        <h1>Add patient</h1>
        <div className="apf-lede">Scan a card to fill the form, or type it in. You can edit everything before adding.</div>
      </div>

      <div
        className={`apf-lens ${dragOn ? "drag" : ""}`}
        data-testid="add-patient-lens"
        onDragOver={(e) => { e.preventDefault(); setDragOn(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOn(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOn(false); }}
        onDrop={(e) => { e.preventDefault(); setDragOn(false); if (e.dataTransfer?.files?.length) scan(e.dataTransfer.files); }}
      >
        <span className="apf-lens-ic"><IconLens size={26} stroke={1.6} /></span>
        <div className="apf-lens-mid">
          <div className="apf-lens-title">Scan ID or insurance card with Lens</div>
          <div className="apf-lens-sub">{scanning ? "Lens is reading your card…" : "Front and back, photo or PDF. Lens reads it into the fields below."}</div>
        </div>
        <div className="apf-lens-right">
          <label className="apf-choose" aria-disabled={scanning ? "true" : undefined} data-testid="add-patient-choose-files">
            <ScanLine size={15} strokeWidth={1.75} /> {scanFiles.length ? "Replace" : "Choose files"}
            <input className="apf-hiddenfile" type="file" multiple accept={ACCEPT_ATTR} disabled={scanning} data-testid="add-patient-file-input"
              onChange={(e) => { scan(e.target.files); e.target.value = ""; }} />
          </label>
          <span className="apf-ordrop">or drop here</span>
        </div>
        {scanFiles.length ? (
          <div className="apf-chips" data-testid="add-patient-chips">
            {scanFiles.map((nm, i) => (
              <span key={i} className="apf-chip" data-testid={`add-patient-chip-${i}`}><IconLens size={13} stroke={1.6} /><span className="apf-chip-nm">{nm}</span></span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="apf-card">
        <div className="apf-grid">
          <Field k="first_name" label="First name" inputRef={firstRef} />
          <Field k="last_name" label="Last name" />
          <Field k="dob" label="Date of birth" placeholder="MM/DD/YYYY" inputRef={dobRef} />
          <Field k="gender" label="Sex">
            <select id="apf-gender" value={f.gender} onChange={set("gender")} className="apf-input" data-testid="add-patient-gender">
              <option value="">Unknown</option>
              <option value="F">Female</option>
              <option value="M">Male</option>
            </select>
          </Field>
          <Field k="phone" label="Phone" placeholder="(718) 555-0100" />
          <Field k="payer_name" label="Primary payer" placeholder="Search payers" />
          <Field k="member_id" label="Member ID" />
        </div>

        {dups ? (
          <div className="apf-dups" data-testid="add-patient-dups">
            <div className="text-[12px] font-bold mb-2" style={{ color: "#9A5A00" }}>A patient with this name &amp; date of birth already exists:</div>
            {(dups.length ? dups : [{ name: "Existing patient" }]).map((d, i) => (
              <div key={i} className="text-[12.5px] py-1" style={{ color: "#141412" }}>{d.name || d.full_name || "Patient"}<span style={{ color: "#6E6E6B" }}>{d.mrn ? ` · ${d.mrn}` : ""}{d.dob ? ` · DOB ${d.dob}` : ""}</span></div>
            ))}
            <button onClick={() => submit(true)} disabled={busy} className="mt-2 text-[12px] font-semibold" style={{ color: "#9A5A00" }} data-testid="add-patient-force">Create a new record anyway</button>
          </div>
        ) : null}

        <div className="apf-foot">
          <button className="apf-primary" onClick={() => submit(false)} disabled={busy} data-testid="add-patient-submit">{busy ? "Adding…" : "Add patient"}</button>
          <button className="apf-secondary" onClick={onClose} data-testid="add-patient-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}
